import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchStats } from "@/lib/api";
import { fetchActivePromotion } from "@/lib/promotions";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { fmtRelative } from "@/lib/format";
import { ArrowRight, BookOpen, MessageSquareQuote, AlertTriangle, Megaphone } from "lucide-react";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activePromo, setActivePromo] = useState(undefined); // undefined=loading, null=none, obj=loaded

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      fetchStats(),
      fetchActivePromotion().catch(() => null),
    ]);
    setStats(s);
    setActivePromo(p);
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Indie Law"
        title="Content engine"
        subtitle="Two linked tables. One purpose: never run out of stories that ladder up to the things you stand for."
        testId="dashboard-header"
      />

      <ThisMonthCard promo={activePromo} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <StatCard
          label="Beliefs"
          value={stats?.total_beliefs}
          sub={`${stats?.active_beliefs || 0} active`}
          to="/beliefs"
          icon={<BookOpen size={18} strokeWidth={2.5} />}
          testId="stat-beliefs"
        />
        <StatCard
          label="Stories"
          value={stats?.total_stories}
          sub="Across all types"
          to="/stories"
          icon={<MessageSquareQuote size={18} strokeWidth={2.5} />}
          testId="stat-stories"
        />
        <StatCard
          label="Gaps"
          value={stats?.gaps_count}
          sub="Active beliefs without a story"
          to="/gaps"
          icon={<AlertTriangle size={18} strokeWidth={2.5} />}
          accent
          testId="stat-gaps"
        />
      </div>

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8 mb-10">
        <div className="text-xs uppercase tracking-[0.1em] text-[#141B4D]/60 mb-2">
          Last sync
        </div>
        <div className="text-2xl font-bold text-[#141B4D]" data-testid="last-sync-display">
          {fmtRelative(stats?.last_sync)}
        </div>
        <p className="text-sm text-[#141B4D]/70 mt-3 max-w-2xl">
          The dashboard syncs from your Google Sheets automatically every 6 hours.
          Hit <span className="font-bold">Sync Now</span> in the top right whenever you
          update either spreadsheet and want changes pulled in immediately.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <QuickLink
          to="/write-beliefs"
          title="Write beliefs with AI"
          desc="Tell Claude what you're promoting and the season. Get a fresh tree of beliefs in your voice, approve the ones that land."
          testId="quick-write-beliefs"
        />
        <QuickLink
          to="/gaps"
          title="Fill the gaps"
          desc="Every active belief with no stories. Click in, hit 'Generate story drafts,' approve. Gap closed."
          testId="quick-gaps"
        />
        <QuickLink
          to="/create"
          title="Create content"
          desc="Pick a belief, pick the supporting stories, choose your format. Get a week of posts, a blog, or a live video script."
          testId="quick-create"
        />
        <QuickLink
          to="/library"
          title="Content library"
          desc="Everything you've generated and saved. Reach for it when batching content or repurposing."
          testId="quick-library"
        />
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, to, icon, accent, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group block border rounded-sm p-6 transition-colors ${
        accent
          ? "bg-[#141B4D] border-[#141B4D] text-[#F2E9DB]"
          : "bg-white border-[#141B4D]/15 hover:bg-[#FFD100]/10"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <div
          className={`p-2 rounded-sm ${
            accent ? "bg-[#FFD100] text-[#141B4D]" : "bg-[#141B4D]/5 text-[#141B4D]"
          }`}
        >
          {icon}
        </div>
        <ArrowRight
          size={14}
          className={accent ? "text-[#F2E9DB]/70" : "text-[#141B4D]/40 group-hover:text-[#141B4D]"}
        />
      </div>
      <div
        className={`text-xs uppercase tracking-[0.1em] mb-2 ${
          accent ? "text-[#F2E9DB]/70" : "text-[#141B4D]/60"
        }`}
      >
        {label}
      </div>
      <div className="text-4xl font-bold tracking-tight">
        {value === undefined ? "—" : value}
      </div>
      <div
        className={`text-xs mt-2 ${
          accent ? "text-[#F2E9DB]/70" : "text-[#141B4D]/60"
        }`}
      >
        {sub}
      </div>
    </Link>
  );
}

function QuickLink({ to, title, desc, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group block bg-white border border-[#141B4D]/15 rounded-sm p-6 hover:border-[#141B4D]/40 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-bold text-[#141B4D]">{title}</h3>
        <ArrowRight
          size={14}
          className="text-[#141B4D]/40 group-hover:text-[#141B4D] transition-transform group-hover:translate-x-1"
        />
      </div>
      <p className="text-sm text-[#141B4D]/70 leading-relaxed">{desc}</p>
    </Link>
  );
}

function fmtMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function ThisMonthCard({ promo }) {
  if (promo === undefined) {
    return (
      <div
        data-testid="this-month-card-loading"
        className="bg-white border border-[#141B4D]/15 rounded-sm p-6 mb-8 text-sm text-[#141B4D]/60"
      >
        Loading this month's promotion…
      </div>
    );
  }
  if (!promo) {
    return (
      <Link
        to="/promotion-focus"
        data-testid="this-month-card-empty"
        className="block bg-white border border-dashed border-[#141B4D]/25 rounded-sm p-6 mb-8 hover:bg-[#141B4D]/5"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#141B4D]/60 mb-1">
              This month
            </div>
            <div className="text-base font-bold text-[#141B4D]">
              No active promotion. Set one up in Promotion Focus.
            </div>
          </div>
          <ArrowRight size={14} className="text-[#141B4D]/40" />
        </div>
      </Link>
    );
  }
  return (
    <Link
      to="/promotion-focus"
      data-testid="this-month-card"
      className="block bg-[#141B4D] border border-[#141B4D] text-[#F2E9DB] rounded-sm p-6 md:p-8 mb-8 hover:bg-[#141B4D]/95 transition-colors"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFD100] text-[#141B4D] p-2 rounded-sm">
            <Megaphone size={16} strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#F2E9DB]/70 mb-1">
              This month · {fmtMonth(promo.month)}
            </div>
            <h2
              data-testid="this-month-promotion-name"
              className="text-2xl font-bold tracking-tight"
            >
              {promo.promotion_name}
            </h2>
          </div>
        </div>
        {promo.offer_tag && (
          <span
            data-testid="this-month-offer-tag"
            className="inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium bg-[#FFD100] text-[#141B4D]"
          >
            {promo.offer_tag}
          </span>
        )}
      </div>

      {promo.headline_belief && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#F2E9DB]/60 mb-1.5">
            Headline belief
          </div>
          <p
            data-testid="this-month-headline-belief"
            className="text-base text-[#F2E9DB] leading-relaxed max-w-3xl"
          >
            {promo.headline_belief.belief_text}
          </p>
        </div>
      )}

      {promo.cta && (
        <div className="pt-4 border-t border-[#F2E9DB]/15">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#F2E9DB]/60 mb-1.5">
            Call to action
          </div>
          <p
            data-testid="this-month-cta"
            className="text-sm text-[#F2E9DB] font-medium"
          >
            {promo.cta}
          </p>
        </div>
      )}
    </Link>
  );
}
